import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2, MessageCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { waLink } from "./vehicleUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass = "w-full rounded-xl border border-brand/20 bg-white px-4 py-3.5 text-sm text-forest placeholder:text-brand/40 outline-none transition-all duration-300 focus:border-gold focus:ring-4 focus:ring-gold/10";
const labelClass = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-brand";

/* Demande libre (location, pièce recherchée par le client, pièce proposée) → arrive dans l'onglet Questions de l'admin. */
const VehicleRequestForm = ({ kind }) => {
  const { t, lang } = useLanguage();
  const { settings } = useSettings();
  const r = t.vehicles.request[kind];
  const [form, setForm] = useState({ name: "", phone: "", email: "", need: "", extra: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const wa = waLink(settings, `${r.waIntro} ${form.need || ""}`.trim());

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/questions`, { name: form.name, phone: form.phone, email: form.email, subject: r.subject, message: `${r.needLabel} : ${form.need}${form.extra ? `\n${r.extraLabel} : ${form.extra}` : ""}`, lang });
      toast.success(r.success);
      setSent(true);
    } catch (err) { toast.error(err.response?.data?.detail || t.vehicles.sell.error); } finally { setSending(false); }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-start gap-6 rounded-3xl border border-brand/10 bg-white p-8 shadow-xl shadow-brand/5 sm:p-12" data-testid={`vehicle-request-${kind}-sent`}>
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-mint text-brand"><CheckCircle2 className="h-8 w-8" /></span>
        <h3 className="font-display text-2xl font-bold text-forest">{r.success}</h3>
        <button onClick={() => { setSent(false); setForm({ name: "", phone: "", email: "", need: "", extra: "" }); }} data-testid={`vehicle-request-${kind}-again`} className="inline-flex items-center gap-2 rounded-full border-2 border-gold px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-white">{r.again}</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5 rounded-3xl border border-brand/10 bg-white p-6 shadow-xl shadow-brand/5 sm:p-10" data-testid={`vehicle-request-${kind}-form`}>
      <div className="grid gap-5 sm:grid-cols-2">
        <div><label className={labelClass}>{t.contact.form.name} *</label><input required value={form.name} onChange={set("name")} data-testid={`vehicle-request-${kind}-name`} className={inputClass} placeholder={t.contact.form.namePh} /></div>
        <div><label className={labelClass}>{t.contact.form.phoneSimple} *</label><input required value={form.phone} onChange={set("phone")} data-testid={`vehicle-request-${kind}-phone`} className={inputClass} placeholder={t.contact.form.phonePh} /></div>
      </div>
      <div><label className={labelClass}>{t.contact.form.emailSimple}</label><input type="email" value={form.email} onChange={set("email")} data-testid={`vehicle-request-${kind}-email`} className={inputClass} placeholder={t.contact.form.emailPh} /></div>
      <div><label className={labelClass}>{r.needLabel} *</label><textarea required rows={3} maxLength={1500} value={form.need} onChange={set("need")} data-testid={`vehicle-request-${kind}-need`} className={`${inputClass} resize-none`} placeholder={r.needPh} /></div>
      <div><label className={labelClass}>{r.extraLabel}</label><input maxLength={300} value={form.extra} onChange={set("extra")} data-testid={`vehicle-request-${kind}-extra`} className={inputClass} placeholder={r.extraPh} /></div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={sending} data-testid={`vehicle-request-${kind}-submit`} className="group inline-flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-xl shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-60">
          {sending ? t.vehicles.sending : r.submit}<Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
        {wa && <a href={wa} target="_blank" rel="noreferrer" data-testid={`vehicle-request-${kind}-whatsapp`} className="inline-flex items-center gap-2 rounded-full border-2 border-brand px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-brand transition-all hover:bg-brand hover:text-white"><MessageCircle className="h-4 w-4" />{t.vehicles.whatsapp}</a>}
      </div>
    </form>
  );
};

export default VehicleRequestForm;
